import type {CollectSource} from '../../mesh/sketch/ISketchObject';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config/SketchGeometryToleranceConfig';
import {SketchClosureEndpointAdapter} from './SketchClosureEndpointAdapter';
import {SketchClosureTopologyBuilder} from './SketchClosureTopologyBuilder';
import type {
    SketchClosureHalfEdge,
    SketchClosureSourceEndpoints,
    SketchClosureTopologyGraph,
    SketchClosureTraversalStep,
} from './SketchClosureTypes';

//------ 草图闭合模块化 开始------
/**
 * 草图闭合解析器。
 *
 * 设计说明：
 * 1. 所有草图绘制、Trim、轮廓采样的闭合判断都应集中到该类；
 * 2. 解析过程先构建平面拓扑图，再在拓扑图上查找包含种子边的闭合环；
 * 3. 返回有序遍历步骤，使后续轮廓采样可以复用同一方向信息，避免判定与显示分叉。
 */
export class SketchClosureResolver {
    /** 端点适配器。 */
    private static readonly endpointAdapter = new SketchClosureEndpointAdapter();
    /** 拓扑构建器。 */
    private static readonly topologyBuilder = new SketchClosureTopologyBuilder(SketchClosureResolver.endpointAdapter);

    /**
     * 基于种子边与候选边解析一条首尾连续闭合的拓扑遍历链。
     *
     * @param seedSources 种子边列表。
     * @param candidateSources 候选边列表。
     * @returns 闭合遍历步骤列表；无法闭合时返回 null。
     */
    public static resolveClosedGeometryTraversal(
        seedSources: readonly CollectSource[],
        candidateSources: readonly CollectSource[] = [],
    ): SketchClosureTraversalStep[] | null {
        const normalizedSeedSources = SketchClosureResolver.normalizeDistinctSources(seedSources);
        if (normalizedSeedSources.length < 1) {
            return null;
        }

        const normalizedCandidateSources = SketchClosureResolver.normalizeDistinctSources([
            ...normalizedSeedSources,
            ...candidateSources,
        ]);
        const topologyGraph = SketchClosureResolver.topologyBuilder.build(normalizedCandidateSources);
        const seedTraversalSteps = SketchClosureResolver.resolveSequentialTraversalSteps(
            normalizedSeedSources,
            topologyGraph,
        );
        if (!seedTraversalSteps) {
            return null;
        }

        const firstSeedHalfEdge = SketchClosureResolver.getHalfEdgeForTraversalStep(
            seedTraversalSteps[0],
            topologyGraph,
        );
        const lastSeedHalfEdge = SketchClosureResolver.getHalfEdgeForTraversalStep(
            seedTraversalSteps[seedTraversalSteps.length - 1],
            topologyGraph,
        );
        if (!firstSeedHalfEdge || !lastSeedHalfEdge) {
            return null;
        }

        if (
            seedTraversalSteps.length >= SKETCH_GEOMETRY_TOLERANCE_CONFIG.minimumClosedSourceCount
            && lastSeedHalfEdge.endKey === firstSeedHalfEdge.startKey
        ) {
            return [...seedTraversalSteps];
        }

        const visitedSourceIds = new Set(normalizedSeedSources.map((source) => source.uuid));
        const normalizedTailCandidateSources = normalizedCandidateSources
            .filter((source) => !visitedSourceIds.has(source.uuid));
        const resolvedTailSteps = SketchClosureResolver.findClosedGeometryTailSteps(
            lastSeedHalfEdge.endKey,
            firstSeedHalfEdge.startKey,
            normalizedTailCandidateSources,
            topologyGraph,
            visitedSourceIds,
        );
        if (!resolvedTailSteps) {
            return null;
        }
        return [...seedTraversalSteps, ...resolvedTailSteps];
    }

    /**
     * 基于种子边与候选边解析一条首尾连续闭合的有序边链。
     *
     * @param seedSources 种子边列表。
     * @param candidateSources 候选边列表。
     * @returns 闭合有序边链；无法闭合时返回 null。
     */
    public static resolveClosedGeometrySources(
        seedSources: readonly CollectSource[],
        candidateSources: readonly CollectSource[] = [],
    ): CollectSource[] | null {
        const traversalSteps = SketchClosureResolver.resolveClosedGeometryTraversal(seedSources, candidateSources);
        if (!traversalSteps) {
            return null;
        }
        return traversalSteps.map((step) => step.source);
    }

    /**
     * 判断集合源对象是否能围成封闭几何。
     *
     * @param sources 待判定的集合源对象列表。
     * @returns 若首尾连续闭合则返回 true，否则返回 false。
     */
    public static canFormClosedGeometry(sources: CollectSource[]): boolean {
        if (!Array.isArray(sources) || sources.length < SKETCH_GEOMETRY_TOLERANCE_CONFIG.minimumClosedSourceCount) {
            return false;
        }
        const traversalSteps = SketchClosureResolver.resolveClosedGeometryTraversal(sources, sources);
        if (!traversalSteps) {
            return false;
        }
        const topologyGraph = SketchClosureResolver.topologyBuilder.build(sources);
        const firstHalfEdge = SketchClosureResolver.getHalfEdgeForTraversalStep(
            traversalSteps[0],
            topologyGraph,
        );
        const lastHalfEdge = SketchClosureResolver.getHalfEdgeForTraversalStep(
            traversalSteps[traversalSteps.length - 1],
            topologyGraph,
        );
        return !!firstHalfEdge && !!lastHalfEdge && lastHalfEdge.endKey === firstHalfEdge.startKey;
    }

    /**
     * 获取遍历步骤在当前方向下的起止端点。
     *
     * @param step 遍历步骤。
     * @returns 当前方向对应的起止端点。
     */
    public static getTraversalStepEndpoints(step: SketchClosureTraversalStep): SketchClosureSourceEndpoints {
        return SketchClosureResolver.endpointAdapter.getTraversalStepEndpoints(step);
    }

    /**
     * 获取集合源对象的起止端点。
     *
     * @param source 集合源对象。
     * @returns 起点与终点。
     */
    public static getSourceEndpoints(source: CollectSource): SketchClosureSourceEndpoints {
        return SketchClosureResolver.endpointAdapter.getSourceEndpoints(source);
    }

    /**
     * 为有序源列表解析一组连续遍历步骤。
     *
     * @param sources 有序边链。
     * @param topologyGraph 闭合拓扑图。
     * @returns 连续遍历步骤列表；无法形成连续链时返回 null。
     */
    private static resolveSequentialTraversalSteps(
        sources: readonly CollectSource[],
        topologyGraph: SketchClosureTopologyGraph,
    ): SketchClosureTraversalStep[] | null {
        if (sources.length < 1) {
            return null;
        }

        const resolveByIndex = (
            sourceIndex: number,
            currentEndKey: string | null,
            collectedSteps: SketchClosureTraversalStep[],
        ): SketchClosureTraversalStep[] | null => {
            if (sourceIndex >= sources.length) {
                return collectedSteps;
            }
            const currentSource = sources[sourceIndex];
            if (!currentSource) {
                return null;
            }
            const candidateHalfEdges = topologyGraph.halfEdgesBySourceId.get(currentSource.uuid) ?? [];
            for (const candidateHalfEdge of candidateHalfEdges) {
                if (currentEndKey && currentEndKey !== candidateHalfEdge.startKey) {
                    continue;
                }
                const resolvedSteps = resolveByIndex(
                    sourceIndex + 1,
                    candidateHalfEdge.endKey,
                    [...collectedSteps, candidateHalfEdge.traversalStep],
                );
                if (resolvedSteps) {
                    return resolvedSteps;
                }
            }
            return null;
        };

        return resolveByIndex(0, null, []);
    }

    /**
     * 递归查找可将当前端点闭合回目标起点的尾部边链。
     *
     * @param currentEndKey 当前链路终点拓扑键。
     * @param targetStartKey 目标闭合起点拓扑键。
     * @param candidateSources 可继续尝试的候选边。
     * @param topologyGraph 闭合拓扑图。
     * @param visitedSourceIds 已访问边 ID 集合。
     * @returns 成功时返回尾部遍历步骤，否则返回 null。
     */
    private static findClosedGeometryTailSteps(
        currentEndKey: string,
        targetStartKey: string,
        candidateSources: readonly CollectSource[],
        topologyGraph: SketchClosureTopologyGraph,
        visitedSourceIds: ReadonlySet<string>,
    ): SketchClosureTraversalStep[] | null {
        for (const candidateSource of candidateSources) {
            if (visitedSourceIds.has(candidateSource.uuid)) {
                continue;
            }
            const candidateHalfEdges = topologyGraph.halfEdgesBySourceId.get(candidateSource.uuid) ?? [];
            for (const candidateHalfEdge of candidateHalfEdges) {
                if (candidateHalfEdge.startKey !== currentEndKey) {
                    continue;
                }

                if (candidateHalfEdge.endKey === targetStartKey) {
                    return [candidateHalfEdge.traversalStep];
                }

                const nextVisitedSourceIds = new Set(visitedSourceIds);
                nextVisitedSourceIds.add(candidateSource.uuid);
                const nextCandidateSources = candidateSources.filter((source) => source.uuid !== candidateSource.uuid);
                const resolvedTailSteps = SketchClosureResolver.findClosedGeometryTailSteps(
                    candidateHalfEdge.endKey,
                    targetStartKey,
                    nextCandidateSources,
                    topologyGraph,
                    nextVisitedSourceIds,
                );
                if (resolvedTailSteps) {
                    return [candidateHalfEdge.traversalStep, ...resolvedTailSteps];
                }
            }
        }
        return null;
    }

    /**
     * 从拓扑图中找到遍历步骤对应半边。
     *
     * @param step 遍历步骤。
     * @param topologyGraph 闭合拓扑图。
     * @returns 匹配的半边；不存在时返回 null。
     */
    private static getHalfEdgeForTraversalStep(
        step: SketchClosureTraversalStep | undefined,
        topologyGraph: SketchClosureTopologyGraph,
    ): SketchClosureHalfEdge | null {
        if (!step) {
            return null;
        }
        const halfEdges = topologyGraph.halfEdgesBySourceId.get(step.source.uuid) ?? [];
        return halfEdges.find((halfEdge) => halfEdge.traversalStep.reversed === step.reversed) ?? null;
    }

    /**
     * 对源列表按 UUID 去重并保留首次出现顺序。
     *
     * @param sources 原始源列表。
     * @returns 去重后的有序源列表。
     */
    private static normalizeDistinctSources(sources: readonly CollectSource[]): CollectSource[] {
        const sourceMap = new Map<string, CollectSource>();
        sources.forEach((source) => {
            if (!sourceMap.has(source.uuid)) {
                sourceMap.set(source.uuid, source);
            }
        });
        return Array.from(sourceMap.values());
    }
}
//------ 草图闭合模块化 结束------
